import { useState, useEffect } from "react";
import { MessageCircle, Send, Share2, Link as LinkIcon, Twitter, Users, Facebook, Instagram } from "lucide-react";
import { toast } from "sonner";
import { shareLinks } from "@/lib/community";
import { api } from "@/lib/apiClient";
import { trackEvent } from "@/lib/analytics";
import ReferralShare from "@/components/ReferralShare";

export default function CommunityShare() {
  const [inviteOpen, setInviteOpen] = useState(false);
  const [links, setLinks] = useState({ whatsapp: "", telegram: "", discord: "", facebook: "", instagram: "" });
  const url = typeof window !== "undefined" ? window.location.origin : "https://apocalypz.org";
  const share = shareLinks(url, "Awaken the ancient knowledge within — Apocalypz.org, for Africa & her Diaspora.");

  useEffect(() => {
    api.get("/settings/community").then((r) => setLinks((l) => ({ ...l, ...r.data }))).catch(() => {});
  }, []);

  const openOr = (link, name) => {
    trackEvent("share_click", { channel: name.toLowerCase(), kind: "join" });
    if (link) window.open(link, "_blank", "noopener");
    else toast.info(`Our ${name} community link is coming soon.`);
  };
  const copyLink = async () => {
    trackEvent("share_click", { channel: "copy_link", kind: "share" });
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
      }
      toast.success("Link copied to clipboard");
    } catch { toast.error("Could not copy link"); }
  };

  return (
    <div className="grid gap-10 md:grid-cols-2" data-testid="community-share">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-gold mb-4">Join the Community</p>
        <p className="text-bone/60 text-sm mb-5 max-w-md">Get teachings, events and updates where our people already gather — connect across the Diaspora on WhatsApp and Telegram.</p>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => openOr(links.whatsapp, "WhatsApp")} data-testid="join-whatsapp"
            className="flex items-center gap-2 bg-[#25D366] text-black px-5 py-2.5 font-medium hover:opacity-90 transition-opacity">
            <MessageCircle size={18} /> WhatsApp
          </button>
          <button onClick={() => openOr(links.telegram, "Telegram")} data-testid="join-telegram"
            className="flex items-center gap-2 bg-[#229ED9] text-white px-5 py-2.5 font-medium hover:opacity-90 transition-opacity">
            <Send size={18} /> Telegram
          </button>
          {links.discord && (
            <button onClick={() => openOr(links.discord, "Discord")} data-testid="join-discord"
              className="flex items-center gap-2 bg-[#5865F2] text-white px-5 py-2.5 font-medium hover:opacity-90 transition-opacity">
              <MessageCircle size={18} /> Discord
            </button>
          )}
          {links.facebook && (
            <button onClick={() => openOr(links.facebook, "Facebook")} data-testid="join-facebook"
              className="flex items-center gap-2 bg-[#1877F2] text-white px-5 py-2.5 font-medium hover:opacity-90 transition-opacity">
              <Facebook size={18} /> Facebook
            </button>
          )}
          {links.instagram && (
            <button onClick={() => openOr(links.instagram, "Instagram")} data-testid="join-instagram"
              className="flex items-center gap-2 bg-gradient-to-r from-[#833AB4] via-[#E1306C] to-[#F77737] text-white px-5 py-2.5 font-medium hover:opacity-90 transition-opacity">
              <Instagram size={18} /> Instagram
            </button>
          )}
        </div>
      </div>

      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-gold mb-4 flex items-center gap-2"><Share2 size={14} /> Share Apocalypz.org</p>
        <p className="text-bone/60 text-sm mb-5 max-w-md">Spread the knowledge. Share the site with family and friends across the world.</p>
        <div className="flex flex-wrap gap-3">
          <a href={share.whatsapp} target="_blank" rel="noreferrer" data-testid="share-whatsapp" onClick={() => trackEvent("share_click", { channel: "whatsapp", kind: "share" })}
            className="flex items-center gap-2 border border-white/15 text-bone/80 px-4 py-2.5 hover:border-gold/50 hover:text-gold transition-colors">
            <MessageCircle size={16} /> WhatsApp
          </a>
          <a href={share.telegram} target="_blank" rel="noreferrer" data-testid="share-telegram" onClick={() => trackEvent("share_click", { channel: "telegram", kind: "share" })}
            className="flex items-center gap-2 border border-white/15 text-bone/80 px-4 py-2.5 hover:border-gold/50 hover:text-gold transition-colors">
            <Send size={16} /> Telegram
          </a>
          <a href={share.twitter} target="_blank" rel="noreferrer" data-testid="share-twitter" onClick={() => trackEvent("share_click", { channel: "twitter", kind: "share" })}
            className="flex items-center gap-2 border border-white/15 text-bone/80 px-4 py-2.5 hover:border-gold/50 hover:text-gold transition-colors">
            <Twitter size={16} /> Post
          </a>
          <button onClick={copyLink} data-testid="share-copy"
            className="flex items-center gap-2 border border-white/15 text-bone/80 px-4 py-2.5 hover:border-gold/50 hover:text-gold transition-colors">
            <LinkIcon size={16} /> Copy link
          </button>
        </div>
        <button onClick={() => setInviteOpen(true)} data-testid="invite-track-button"
          className="mt-4 inline-flex items-center gap-2 text-gold hover:text-gold2 text-sm transition-colors">
          <Users size={16} /> Invite friends &amp; track your impact →
        </button>
      </div>
      <ReferralShare open={inviteOpen} onClose={() => setInviteOpen(false)} />
    </div>
  );
}
